import React from 'react';
import { ArrowDown, ExternalLink, FileCode } from 'lucide-react';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';

function fileHref(baseUrl, path) {
  if (!baseUrl || !path) return null;
  if (!/^[\w./-]+$/.test(path)) return null;
  return `${baseUrl.replace(/\/$/, '')}/${path}`;
}

export function ProjectArchitecture({ architecture, repoBaseUrl }) {
  if (!architecture?.layers?.length) return null;

  return (
    <div className="space-y-6">
      {architecture.summary && (
        <p className="text-sm text-[var(--fg-secondary)] leading-relaxed">{architecture.summary}</p>
      )}

      <div className="space-y-4">
        {architecture.layers.map((layer, index) => {
          const isLast = index === architecture.layers.length - 1;

          return (
            <div key={layer.label}>
              <Card className="p-5">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                  <div className="sm:w-40 sm:shrink-0">
                    <span className="text-xs font-semibold uppercase tracking-wider text-[var(--fg-primary)] block">
                      {layer.label}
                    </span>
                    {layer.caption && (
                      <p className="mt-1.5 text-xs text-[var(--fg-secondary)] leading-relaxed">{layer.caption}</p>
                    )}
                  </div>

                  <div className="flex flex-1 flex-wrap gap-2">
                    {layer.nodes.map((node) => {
                      const href = fileHref(repoBaseUrl, node.path);
                      const Wrapper = href ? 'a' : 'div';
                      const linkProps = href
                        ? { href, target: '_blank', rel: 'noopener noreferrer' }
                        : {};

                      return (
                        <Wrapper
                          key={node.name}
                          {...linkProps}
                          className={`rounded-md bg-[var(--bg-recessed)] shadow-[var(--border-ring)] px-3 py-2 transition-colors ${
                            href ? 'cursor-pointer hover:bg-[var(--bg-hover)]' : ''
                          }`}
                        >
                          <span className="block text-xs font-medium text-[var(--fg-primary)]">{node.name}</span>
                          {node.meta && (
                            <span className="mt-0.5 flex items-center gap-1 font-mono text-[11px] text-[var(--fg-secondary)]">
                              {node.meta}
                              {href && <ExternalLink size={10} />}
                            </span>
                          )}
                        </Wrapper>
                      );
                    })}
                  </div>
                </div>
              </Card>

              {!isLast && (
                <div className="flex items-center justify-center gap-2 py-2">
                  <ArrowDown size={14} className="text-[var(--fg-muted)]" />
                  {layer.transport && (
                    <span className="font-mono text-[11px] text-[var(--fg-secondary)]">{layer.transport}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {architecture.notes?.length > 0 && (
        <ul className="grid gap-2 sm:grid-cols-2 pt-2">
          {architecture.notes.map((note) => (
            <li key={note} className="flex items-start gap-2">
              <span className="text-[var(--fg-muted)] select-none text-xs leading-relaxed">—</span>
              <span className="text-xs text-[var(--fg-secondary)] leading-relaxed">{note}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ProjectCodeMap({ codeMap }) {
  if (!codeMap?.entries?.length) return null;

  return (
    <Card className="mt-6">
      <h3 className="t-h6 text-[var(--fg-primary)] mb-1">Explore the Code</h3>
      <p className="text-xs text-[var(--fg-secondary)] mb-4">
        Direct links to files implementing the functionality described above.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {codeMap.entries.map((entry) => (
          <a
            key={entry.path}
            href={`${codeMap.baseUrl.replace(/\/$/, '')}/${entry.path}`}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md shadow-[var(--border-ring)] bg-[var(--bg-recessed)] p-3 transition-colors hover:bg-[var(--bg-hover)] cursor-pointer"
          >
            <div className="flex items-center gap-2 mb-1">
              <FileCode size={14} className="text-[var(--fg-secondary)]" />
              <code className="font-mono text-[var(--fg-primary)] break-all">{entry.path}</code>
              <ExternalLink size={12} className="ml-auto text-[var(--fg-secondary)]" />
            </div>
            <p className="text-xs font-medium text-[var(--fg-primary)] mt-1">{entry.title}</p>
            <p className="text-xs text-[var(--fg-secondary)] leading-relaxed mt-0.5">{entry.description}</p>
          </a>
        ))}
      </div>
    </Card>
  );
}
